import {
  type OverviewPayload,
  PreferredFileTypes,
  type RepoOverviewBlobPayload,
  type TreePayload,
} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {repositoryTreePath} from '@github-ui/paths'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Link} from '@github-ui/react-core/link'
import {Box, Button, Flash, Link as PrimerLink, Text} from '@primer/react'
import {useCallback, useState} from 'react'

import {useOpenPanel} from '../contexts/OpenPanelContext'
import {CurrentTreeProvider} from '../hooks/CurrentTree'
import {useFilesPageInfo} from '../hooks/FilesPageInfo'
import {BranchInfoBar} from './directory/BranchInfoBar/BranchInfoBar'
import DirectoryContent from './directory/DirectoryContent'
import {DirectoryReadmePreview} from './directory/DirectoryContent/DirectoryReadmePreview'
import {Dropzone} from './directory/Dropzone'
import FolderViewHeader from './headers/FolderViewHeader'
import {LatestCommitSingleLine} from './LatestCommit'
import {OverviewFiles} from './overview/OverviewRepoFiles'

export function FileTreeViewContent({
  tree,
  overview,
  showTree,
  treeToggleElement,
  expandTree,
  onFindFilesShortcut,
}: {
  tree: TreePayload
  overview?: OverviewPayload
  showTree: boolean
  treeToggleElement: JSX.Element | null
  expandTree: () => void
  onFindFilesShortcut?: () => void
}) {
  const repo = useCurrentRepository()
  const {refInfo, path} = useFilesPageInfo()
  const uploadUrl = repositoryTreePath({
    repo,
    commitish: refInfo.name,
    path,
    action: 'upload',
  })
  const {openPanel, setOpenPanel} = useOpenPanel()
  const [hideFiles, setHideFiles] = useState(overview?.hideRepoFiles)

  const onViewSourceClick = useCallback(() => {
    setHideFiles(false)
  }, [])

  const readme = overview
    ? overview.overviewFiles.find(file => file.preferredFileType === PreferredFileTypes.README)
    : tree.readme

  const coc = overview?.overviewFiles.find(file => file.preferredFileType === PreferredFileTypes.CODE_OF_CONDUCT)
  const licenses = overview?.overviewFiles.filter(file => file.preferredFileType === PreferredFileTypes.LICENSE)
  const securityPolicy = overview?.overviewFiles.find(file => file.preferredFileType === PreferredFileTypes.SECURITY)

  return (
    <CurrentTreeProvider payload={tree}>
      <LatestCommitSingleLine commitCount={overview?.commitCount} />
      {tree.showBranchInfobar && (
        <ErrorBoundary fallback={<BranchInfoBarErrorBanner />}>
          <BranchInfoBar />
        </ErrorBoundary>
      )}
      {!overview && <FolderViewHeader showTree={showTree} treeToggleElement={treeToggleElement} />}
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
        {hideFiles ? (
          <PrimerLink onClick={onViewSourceClick}>View stack template source</PrimerLink>
        ) : (
          <Box>
            <DirectoryContent
              overview={overview}
              showTree={showTree}
              expandTree={expandTree}
              treeToggleElement={treeToggleElement}
              onFindFilesShortcut={onFindFilesShortcut}
            />
          </Box>
        )}
        {overview && (
          <OverviewFiles
            readme={readme as RepoOverviewBlobPayload}
            coc={coc}
            licenses={licenses ?? []}
            securityPolicy={securityPolicy}
            shouldRecommendReadme={overview.banners.shouldRecommendReadme}
            isPersonalRepo={overview.banners.isPersonalRepo}
            processingTime={overview.overviewFilesProcessingTime}
          />
        )}
        {readme && !overview && (
          <DirectoryReadmePreview
            openPanel={openPanel}
            setOpenPanel={setOpenPanel}
            readme={readme}
            stickyHeaderHeight={50}
          />
        )}
        {overview && !readme && overview.banners.shouldRecommendReadme && (
          <Flash
            sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}
            className={'file-tree-view-readme-flash-narrow'}
          >
            <Text>
              {overview.banners.isPersonalRepo
                ? 'Add a README with an overview of your project.'
                : 'Help people interested in this repository understand your project by adding a README.'}
            </Text>
            <Button
              as={Link}
              to={`${repositoryTreePath({
                repo,
                path: undefined,
                commitish: refInfo.name,
                action: 'new',
              })}?filename=README.md`}
              reloadDocument={true}
            >
              Add a README
            </Button>
          </Flash>
        )}

        {repo.currentUserCanPush && <Dropzone uploadUrl={uploadUrl} />}
      </Box>
    </CurrentTreeProvider>
  )
}

function BranchInfoBarErrorBanner() {
  return (
    <Flash variant="warning" sx={{my: 3}}>
      <Text>Cannot retrieve comparison with upstream repository.</Text>
    </Flash>
  )
}

try{ FileTreeViewContent.displayName ||= 'FileTreeViewContent' } catch {}
try{ BranchInfoBarErrorBanner.displayName ||= 'BranchInfoBarErrorBanner' } catch {}